///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthAbstractCommand } from '../abstract-command';

import { ActivityDTO } from '../../../../services/project-socket/activity-dto.model';
import { GtthPreloaderModalEvent } from '../../../../shared/preloader-modal/preloader-modal-event';
import { GtthRemoveLinkCommand } from './link/remove-link-command';
import { GtthRemoveTerminalCommand } from './node/remove-terminal-command';
import { GtthCreateMultipleEntitiesCommand } from './create-multiple-entities-command';
import { LinkFeature } from '../../entities/link/link-feature';
import { GtthNodeFeatureDtoModel } from '../../../../services/project-socket/dto/gtth-node-feature-dto.model';
import { GtthLinkFeatureDtoModel } from '../../../../services/project-socket/dto/gtth-link-feature-dto.model';
import { NodeFeature } from '../../entities/node/node-feature';

export class GtthRemoveMultipleEntitiesCommand extends GtthAbstractCommand {
    private linkIds: number[];
    private terminalIds: number[];

    private terminalUnitsToRemove: GtthNodeFeatureDtoModel[];
    private linksToRemove: GtthLinkFeatureDtoModel[];
    private _isClearDesign: boolean;

    create(terminalIds: number[], linkIds: number[]): this {
        this.terminalIds = terminalIds;
        this.linkIds = linkIds;
        return this;
    }

    createFromObjects(stations: NodeFeature[], links: LinkFeature[]): this {
        this.terminalIds = [];
        this.linkIds = [];

        this.linksToRemove = [];
        links ??= [];
        links.forEach((linkToRemove: LinkFeature) => {
            if (!linkToRemove.isUserForced()) {
                this.linksToRemove.push(linkToRemove.toGeoJson());
            }
        });

        this.terminalUnitsToRemove = [];
        stations ??= [];
        stations.forEach((terminalStation: NodeFeature) => {
            if (!terminalStation.hasFreezeLinks()) {
                this.terminalUnitsToRemove.push(terminalStation.toGeoJson());
            }
        });

        return this;
    }

    getCommandName(): string {
        return 'GtthRemoveMultipleEntitiesCommand';
    }

    doCommand(): void {
        this.historyService.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.show('Loading')
        });

        const logicalTopology = this.historyService.logicalTopology;

        if (!this.linkIds || !this.terminalIds) {
            this.linkIds = this.historyService.logicalTopology.getSelectedNotFrozenLinks();
            this.terminalIds = [];

            this.historyService.logicalTopology.getSelectedNotFrozenStations().forEach((stationId: number) => {
                this.terminalIds.push(stationId);
                const terminalStation = logicalTopology.getStation(stationId);
                if (terminalStation.linkIds && terminalStation.linkIds.size > 0) {
                    terminalStation.linkIds.forEach((linkId: number) => {
                        if (!this.linkIds.find((id) => id === linkId)) {
                            this.linkIds.push(linkId);
                        }
                    });
                }
            });
        }

        if (!this.linksToRemove) {
            this.linksToRemove = [];

            this.linkIds.forEach((entityId: number) => {
                const linkToRemove = logicalTopology.getLink(entityId);
                if (!linkToRemove.isUserForced()) {
                    this.linksToRemove.push(linkToRemove.toGeoJson());
                }
            });
        }

        if (!this.terminalUnitsToRemove) {
            this.terminalUnitsToRemove = [];

            this.terminalIds.forEach((entityId: number) => {
                const terminalStation = logicalTopology.getStation(entityId);
                if (!terminalStation.hasFreezeLinks()) {
                    this.terminalUnitsToRemove.push(terminalStation.toGeoJson());
                }
            });
        }

        if (this.historyService.selectAreaService.overlay) {
            this.historyService.selectAreaService.destroyOverlay();
        }

        this._isClearDesign = false;
        let activeNode;
        const activeLink = this.linksToRemove.find((link: GtthLinkFeatureDtoModel) => {
            const nodeFromIsEnabled = this.historyService.logicalTopology.getStation(link.getNode0IdCustom()).isEnabled();
            const nodeToIsEnabled = this.historyService.logicalTopology.getStation(link.getNode1IdCustom()).isEnabled();
            return !link.getIsDisabled() || nodeFromIsEnabled || nodeToIsEnabled;
        });
        if (!activeLink) {
            activeNode = this.terminalUnitsToRemove.find((node: GtthNodeFeatureDtoModel) => {
                return node.getIsEnabled();
            });
        }
        if (activeLink || activeNode) {
            this._isClearDesign = true;
        }

        const activity = this.createActivityDTO(false);
        activity.links = [...this.linksToRemove];
        activity.links.forEach((link) => link.setToRemove(true));
        activity.nodes = [...this.terminalUnitsToRemove];
        activity.nodes.forEach((node) => node.setToRemove(true));
        this.sendActivity(activity);
    }

    undoCommand(): void {
        this.terminalUnitsToRemove.forEach((node) => node.setToRemove(false));
        this.linksToRemove.forEach((link) => link.setToRemove(false));

        const createMultipleCommand = this.historyService
            .command(GtthCreateMultipleEntitiesCommand)
            .create(this.terminalUnitsToRemove, this.linksToRemove);

        this.historyService.doCommandBypassHistory(createMultipleCommand).then((result: boolean) => {
            if (this.commandResolve) {
                this.commandResolve(result);
            }
        });
    }

    doCommandActivity(activityDto: ActivityDTO): void {
        if (!activityDto.successful) {
            this.historyService.eventManager.broadcast({
                name: 'togglePreloaderModal',
                eventObject: GtthPreloaderModalEvent.hide()
            });

            this.historyService.notificationService.showError(activityDto?.error?.message);

            if (this.terminalUnitsToRemove && this.linksToRemove) {
                console.log('Command failed:', activityDto.error);
            }

            if (this.commandResolve) {
                this.commandResolve(activityDto.successful);
            }
        } else {
            const nodesRemoveCommands = [];
            const linksRemoveCommands = [];

            activityDto.links ??= [];
            activityDto.links.forEach((linkGeoJson: GtthLinkFeatureDtoModel) =>
                linksRemoveCommands.push(
                    this.historyService.doCommandBypassHistory(
                        this.historyService.command(GtthRemoveLinkCommand).createFromId(linkGeoJson.getId()),
                        true
                    )
                )
            );

            Promise.all(linksRemoveCommands).then((_linksRemoveResult) => {
                activityDto.nodes ??= [];
                activityDto.nodes.forEach((terminalGeoJson: GtthNodeFeatureDtoModel) =>
                    nodesRemoveCommands.push(
                        this.historyService.doCommandBypassHistory(
                            this.historyService.command(GtthRemoveTerminalCommand).createFromId(terminalGeoJson.getId()),
                            true
                        )
                    )
                );

                Promise.all(nodesRemoveCommands).then((_nodesRemoveResult) => {
                    if (this.commandResolve) {
                        this.commandResolve(true);
                    }

                    this.historyService.eventManager.broadcast({
                        name: 'togglePreloaderModal',
                        eventObject: GtthPreloaderModalEvent.hide()
                    });
                });
            });
        }
    }

    isDisabledInReadOnlyMode(): boolean {
        return true;
    }

    isModifyTopology(): boolean {
        return true;
    }

    isClearDesign(): boolean {
        return this._isClearDesign;
    }
}
